import cx from 'classnames'
import { useState } from 'react'

import { DOMAINS_WITH_USER_ACCOUNT } from '@js/common/constants'
import { getUrlByDomain } from '@js/common/getUrlByDomain'
import { UA } from '@js/common/UA'
import { isClientSide } from '@js/common/utils'

import CTA from '@f/elements/CTA/CTA'

import { useClientOnly } from '@f-fertighaus/components/ClientOnly/ClientOnly'
import MobileNavigationMenuItem from '@f-fertighaus/layout/PageHeader/partials/MobileMenuItem'

import { MenuItem, MenuItemType, usePageHeader } from '../PageHeaderSSR'

interface MobileNavProps {
  currentActiveMenu: MenuItemType | null
  handleMenuItemClick: (menuItem: MenuItemType) => void
  closeAllMenus: (options?: { forceCloseCurrentMenu?: boolean }) => void
  menuItems: MenuItem[]
  postcode: string
  quizCTA: {
    label: string
    href: string
  }
  partnershipCTA: {
    label: string
    href: string
  }
}

export const MobileNav = ({
  handleMenuItemClick,
  closeAllMenus,
  currentActiveMenu,
  menuItems,
  quizCTA,
  partnershipCTA,
  postcode
}: MobileNavProps) => {
  const [submenuOpen, setSubmenuOpen] = useState('')
  const [isLoggedIn, setLoggedIn] = useState(UA.isLoggedIn())
  const isMounted = useClientOnly()
  const { domain } = usePageHeader()
  const getUrl = getUrlByDomain(domain)
  const toggleMenu = (
    event: React.MouseEvent<HTMLLIElement>,
    title: string
  ) => {
    event.preventDefault()
    const lowerCasedSubmenuTitle = title.toLowerCase()
    const titleMatchesCurrent = submenuOpen === lowerCasedSubmenuTitle
    const newSubmenuValue = titleMatchesCurrent ? '' : lowerCasedSubmenuTitle
    setSubmenuOpen(newSubmenuValue)
  }

  const handleLinkClick = () => {
    closeAllMenus()
  }

  const isMenuActiveAndNotSearch =
    currentActiveMenu && currentActiveMenu !== 'search'
  return (
    <div className='[ f-pos-rel ] PageHeader-menu-wrapper PageHeader-menu-wrapper-burger'>
      <CTA
        type='icon'
        onClick={() => {
          if (currentActiveMenu) {
            closeAllMenus({
              forceCloseCurrentMenu: true
            })
          }
          handleMenuItemClick(isMenuActiveAndNotSearch ? '' : 'mobile')
        }}
        className={cx(
          '[ f-p-a-010 f-m-v-010 ]',
          'PageHeader-button PageHeader-with-submenu PageHeader-menu-toggle',
          currentActiveMenu && 'is-open'
        )}
        data-sel='btn-nav-mbl'
        wtData={{
          'CTA-click': {
            location: 'header'
          }
        }}>
        <svg
          aria-hidden='true'
          role='img'
          focusable='false'
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 16 16'
          className={cx(
            'PageHeader-button-icon PageHeader-icon PageHeader-mobile-menu-icon'
          )}>
          <path
            vectorEffect='non-scaling-stroke'
            d='M.5 2h15'
            style={
              isMenuActiveAndNotSearch
                ? { transform: 'rotate(45deg)', transformOrigin: '1px 5px' }
                : { transform: 'rotate(0deg)', transformOrigin: '1px 5px' }
            }
          />
          <path
            vectorEffect='non-scaling-stroke'
            d='M.5 8h15'
            data-pos='mid'
            style={isMenuActiveAndNotSearch ? { opacity: 0 } : {}}
          />
          <path
            vectorEffect='non-scaling-stroke'
            d='M.5 14h15'
            data-pos='bot'
            style={
              isMenuActiveAndNotSearch
                ? { transform: 'rotate(-45deg)', transformOrigin: '1px 11px' }
                : { transform: 'rotate(0deg)', transformOrigin: '1px 11px' }
            }
          />
        </svg>
      </CTA>

      <nav
        className={cx(
          '[ f-c-ba-whi ] PageHeader-menu',
          currentActiveMenu && 'is-active'
        )}
        style={{
          display: isMenuActiveAndNotSearch ? 'block' : 'none'
        }}>
        <ul className='f-!list'>
          {menuItems.map(menuItem => (
            <MobileNavigationMenuItem
              key={`desktop_menu_${menuItem.title}`}
              menuItem={menuItem}
              activeMenuType={currentActiveMenu}
              isOpen={submenuOpen === menuItem.title.toLowerCase()}
              postcode={postcode}
              onTopHeaderClick={
                menuItem.sub_menu_items
                  ? (e: React.MouseEvent<HTMLLIElement>) => {
                      toggleMenu(e, menuItem.title)
                      handleMenuItemClick(menuItem.type)
                    }
                  : handleLinkClick
              }
              onSubMenuClick={handleLinkClick}
            />
          ))}
          <div className='PageHeader-menu-mobile-divider f-m-h-030' />
          {isMounted && !UA.isLoggedIn() && (
            <li className='[ f-m-h-030 f-c-bo-neu050 f-m-t-020 ]'>
              <CTA
                tag='a'
                type='block-primary'
                className='[ f-t-body ] is-full-width'
                href={quizCTA.href}
                wtData={{
                  'CTA-click': {
                    location: 'header',
                    label: 'vendor-quiz-standalone',
                    indicates: ['funnel-entry']
                  }
                }}>
                {quizCTA.label}
              </CTA>
            </li>
          )}
          {isMounted && (
            <>
              <li
                key={`mobile_menu_baupartner`}
                className={` f-m-h-030 f-c-bo-neu050`}>
                <CTA
                  tag='a'
                  href={partnershipCTA.href}
                  type='text-primary'
                  className={cx(
                    'f-t-tbody f-p-v-020 f-pos-rel f-t-left is-block',
                    isClientSide &&
                      window.location.pathname.indexOf('baupartner-werden') !==
                        -1 &&
                      'is-active'
                  )}
                  onClick={handleLinkClick}
                  wtData={{
                    'CTA-click': {
                      location: 'header'
                    }
                  }}>
                  {partnershipCTA.label}
                </CTA>
              </li>
              {DOMAINS_WITH_USER_ACCOUNT.includes(domain) && (
                <>
                  {isLoggedIn ? (
                    <>
                      <li
                        key={`mobile_menu_projekt`}
                        className={` f-m-h-030 f-c-bo-neu050`}>
                        <CTA
                          tag='a'
                          href={getUrl('userAccount')()}
                          type='text-primary'
                          className={cx(
                            'f-t-tbody f-p-b-020 f-pos-rel f-t-left is-block'
                          )}
                          onClick={handleLinkClick}
                          wtData={{
                            'CTA-click': {
                              location: 'header'
                            }
                          }}>
                          Zu meinem Projekt
                        </CTA>
                      </li>
                      <li
                        key={`mobile_menu_projekt`}
                        className={` f-m-h-030 f-c-bo-neu050`}>
                        <CTA
                          tag='a'
                          href={getUrl('userProfile')()}
                          type='text-primary'
                          className={cx(
                            'f-t-tbody f-p-b-020 f-pos-rel f-t-left is-block'
                          )}
                          onClick={handleLinkClick}
                          wtData={{
                            'CTA-click': {
                              location: 'header'
                            }
                          }}>
                          {/(massivhaus)|(bungalow)/.test(window.__DOMAIN__)
                            ? 'Mein Konto'
                            : 'Mein Profil'}
                        </CTA>
                      </li>
                      <li
                        key={`mobile_menu_log_out`}
                        className={` f-m-h-030 f-c-bo-neu050`}>
                        <CTA
                          tag='a'
                          type='text-primary'
                          className={cx(
                            'f-t-tbody f-p-b-020 f-pos-rel f-t-left is-block'
                          )}
                          onClick={() => {
                            setLoggedIn(false)
                            UA.doLogOut()
                            handleLinkClick()
                          }}
                          wtData={{
                            'CTA-click': {
                              location: 'header'
                            }
                          }}>
                          Ausloggen
                        </CTA>
                      </li>
                    </>
                  ) : (
                    <li
                      key={`mobile_menu_log_in`}
                      className={` f-m-h-030 f-c-bo-neu050`}>
                      <CTA
                        tag='a'
                        href={getUrl('userAccount')()}
                        type='text-primary'
                        className={cx(
                          'f-t-tbody f-p-b-020 f-pos-rel f-t-left is-block'
                        )}
                        onClick={handleLinkClick}
                        wtData={{
                          'CTA-click': {
                            location: 'header'
                          }
                        }}>
                        Anmelden
                      </CTA>
                    </li>
                  )}
                </>
              )}
            </>
          )}
        </ul>
      </nav>
    </div>
  )
}
